
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { clearSsoReturnContext, getSsoQueryParam, isSilentAuthError, readSsoReturnContext, redirectToSsoProvider } from '@/utils/sso';
import { fireGoogleTag } from '@/utils/google-tag-manager-helpers';
import { getRestaurantMetaInfo } from '@/utils/meta';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';

const namespace: string = 'restaurant';

@Component<LoginCallback>({
	components: {
		LoadingSpinner
	},
	metaInfo() {
		return getRestaurantMetaInfo(this.restaurant);
	}
})
export default class LoginCallback extends Vue {
	@Action('fetchRestaurant', { namespace }) private fetchRestaurant!: (payload: FetchRestaurantPayload) => Promise<boolean>;
	@Action('ssoLogin', { namespace: 'auth' }) private ssoLogin!: (payload: SsoLoginPayload) => Promise<string | undefined>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('getRestaurantSso', { namespace }) private restaurantSso!: Sso | null;

	private async created(): Promise<void> {
		const code: string = getSsoQueryParam(this.$route.query.code);
		const state: string = getSsoQueryParam(this.$route.query.state);
		const error: string = getSsoQueryParam(this.$route.query.error);
		const returnContext: SsoReturnContext | null = readSsoReturnContext();

		// Without the stashed context there is nowhere to return to and no state to validate against
		if (!returnContext) {
			this.failCallback('missing_context');
			return;
		}

		try {
			// Once we're on this page, this means we came from a SSO auth URL, so we no longer have the restaurant info, we need to fetch it again.
			await this.fetchRestaurant({
				restaurantSlug: returnContext.slug,
				route: { ...this.$route, query: returnContext.query },
				router: this.$router
			});
		}
		catch (fetchError) {
			this.failCallback('restaurant_fetch_failed');
			return;
		}

		if (!this.restaurantSso) {
			this.failCallback('no_sso_config');
			return;
		}

		if (error) {
			await this.handleProviderError(error, returnContext);
			return;
		}

		// The state has to match the one issued before the redirect, otherwise this is a crafted
		// callback trying to sign the user in with someone else's authorization code
		if (!code || !state || state !== returnContext.state) {
			this.failCallback('state_mismatch');
			return;
		}

		let redirectSlug: string | undefined;

		try {
			redirectSlug = await this.ssoLogin({ provider: this.restaurantSso.type, code, state, codeVerifier: returnContext.codeVerifier });
		}
		catch (loginError) {
			this.failCallback('exchange_failed');
			return;
		}

		// No slug means no location under this provider accepts their role, and signing in again
		// will not change that
		if (!redirectSlug) {
			this.failCallback('role_not_permitted');
			return;
		}

		// Usually the slug they started on, since a privileged user is allowed to stay on a menu
		// below their level. Replay the query either way, the provider only sends back code and state
		clearSsoReturnContext();
		this.$router.replace({ path: `/${redirectSlug}`, query: returnContext.query }).catch(() => {});
	}

	/**
	 * Handle an error coming back from the provider. A refused silent authentication is retried
	 * once interactively, so a user whose provider session expired gets a login screen instead of
	 * a dead end. Every other error, and a second failure, ends on the access denied view.
	 *
	 * @param {string} error
	 * @param {SsoReturnContext} returnContext
	 * @return {Promise<void>}
	 */
	private async handleProviderError(error: string, returnContext: SsoReturnContext): Promise<void> {
		if (!isSilentAuthError(error) || returnContext.retried) {
			this.failCallback('provider_error', error);
			return;
		}

		fireGoogleTag({ name: 'socialLoginOrRegisterError', specifier: this.restaurantSso!.type, detail: 'silent_auth_failed', parameter: error });

		try {
			await redirectToSsoProvider(this.restaurantSso!, returnContext.slug, returnContext.query, false, true);
		}
		catch (challengeError) {
			this.failCallback('provider_error', error);
		}
	}

	/**
	 * Abandon the SSO round trip and show the access denied view.
	 *
	 * @param {string} reason
	 * @param {string} [detail]
	 * @return {void}
	 */
	private failCallback(reason: string, detail?: string): void {
		fireGoogleTag({
			name: 'socialLoginOrRegisterError',
			specifier: this.restaurantSso ? this.restaurantSso.type : undefined,
			detail: reason,
			parameter: detail
		});

		clearSsoReturnContext();
		this.$router.replace({ name: 'AccessDenied' }).catch(() => {});
	}
}
